import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import "./ooraInfraDataset.css";
import { viewOoraInfraDataset } from "../../Redux/Actions/ooraInfraDatasetActions";
import { formatDate } from "../../Utils/ooraInfraDatasetHelpers";

const ViewOoraInfraDataset = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { ooraInfraDataset, singleError } = useSelector(
    (state) => state.ooraInfraDataset || {}
  );

  const dataset = ooraInfraDataset?.data;
  const ready = dataset?._id === id;

  useEffect(() => {
    dispatch(viewOoraInfraDataset(id));
  }, [dispatch, id]);

  // open file in a new tab instead of downloading
  const openDocument = async (e, url) => {
    e.preventDefault();
    const win = window.open("", "_blank");
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const isPdf = url.toLowerCase().split("?")[0].endsWith(".pdf");
      const objUrl = URL.createObjectURL(new Blob([blob], { type: isPdf ? "application/pdf" : blob.type }));
      win.location.href = objUrl;
    } catch (error) {
      win.location.href = url; // fallback (CORS blocked)
    }
  };

  const hasRange = dataset?.date_range?.start_date || dataset?.date_range?.end_date;

  return (
    <>
      <div className="oid-wrapper">
        <div className="oid-header">
          <div className="oid-header-left">
            <div className="oid-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
              </svg>
            </div>
            <div>
              <h1 className="oid-title">{ready ? dataset.title : "Dataset Details"}</h1>
              <p className="oid-subtitle">View uploaded infrastructure documents</p>
            </div>
          </div>
          <div className="oid-actions">
            <button type="button" className="oid-btn oid-btn--ghost" onClick={() => navigate("/oora-infra-dataset")}>
              Back
            </button>
            {ready && (
              <button
                type="button"
                className="oid-btn oid-btn--submit"
                onClick={() =>
                  navigate(`/oora-infra-dataset/update/${dataset._id}`, { state: { row: dataset } })
                }
              >
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="oid-card">
          {!ready ? (
            <div className="oid-empty" style={{ padding: 48, textAlign: "center" }}>
              {singleError ? "Dataset not found" : "Loading..."}
            </div>
          ) : (
            <div className="oid-form-body">
              <div className="oid-meta">
                <div>
                  <span className="oid-label">Created</span>
                  <p>{formatDate(dataset.created_at)}</p>
                </div>
                <div>
                  <span className="oid-label">Updated</span>
                  <p>{formatDate(dataset.updated_at)}</p>
                </div>
                <div>
                  <span className="oid-label">Documents</span>
                  <p>{dataset.documents?.length || 0}</p>
                </div>
              </div>

              <div className="oid-meta">
                <div>
                  <span className="oid-label">Date</span>
                  <p>{formatDate(dataset.date)}</p>
                </div>
                <div>
                  <span className="oid-label">Year</span>
                  <p>{dataset.year || "-"}</p>
                </div>
                <div>
                  <span className="oid-label">Date Range</span>
                  <p>
                    {hasRange
                      ? `${formatDate(dataset.date_range?.start_date)} → ${formatDate(dataset.date_range?.end_date)}`
                      : "-"}
                  </p>
                </div>
              </div>

              {[
                ["Description", dataset.description],
                ["Research Methodology", dataset.research_methodology],
                ["Note", dataset.note],
              ].map(([label, value]) => (
                <div key={label}>
                  <span className="oid-label">{label}</span>
                  <p className="oid-text-block">{value || "-"}</p>
                </div>
              ))}

              <div>
                <span className="oid-label">Source Link</span>
                <p className="oid-text-block">
                  {dataset.source_link ? (
                    <a href={dataset.source_link} target="_blank" rel="noreferrer" className="oid-view-link">
                      {dataset.source_link}
                    </a>
                  ) : (
                    "-"
                  )}
                </p>
              </div>

              <div className="oid-docs-head">
                <h3>Documents</h3>
              </div>

              {dataset.documents?.map((d, i) => (
                <div className="oid-doc oid-doc-view" key={d._id || i}>
                  <div>
                    <span className="oid-section-badge">{String(i + 1).padStart(2, "0")}</span>
                    <p className="oid-doc-view-title">{d.title}</p>
                    {d.description && <p className="oid-doc-view-desc">{d.description}</p>}
                  </div>
                  <a
                    href={d.url}
                    onClick={(e) => openDocument(e, d.url)}
                    target="_blank"
                    rel="noreferrer"
                    className="oid-btn oid-btn--ghost oid-btn--sm"
                  >
                    Open
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ViewOoraInfraDataset;